import { adminHomePath, type Feature } from '@/shared/registry'
import {
  BookOpen,
  Briefcase,
  Network,
  Bell,
  CheckSquare,
  ClipboardCheck,
  ClipboardList,
  DownloadCloud,
  FileText,
  FlaskConical,
  GraduationCap,
  Handshake,
  KeyRound,
  Link2,
  Lock,
  Megaphone,
  MonitorPlay,
  Package,
  Paintbrush,
  PlayCircle,
  RefreshCcw,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Ticket,
  Trophy,
  Users,
  UsersRound,
  Video,
} from '@/shared/ui/icons'

/**
 * Admin Home: the page, and its sections and categories from the mockup's
 * `site-administration/adminHomeData.js`, in its order. Each area adds its own links to these
 * categories from its own `index.ts`, by category id; a category or section with no link shows
 * nowhere. An area may also add a category of its own, such as Channels' Integrations, which
 * comes after these in its section.
 */
export const feature: Feature = {
  id: 'admin-home',
  routes: [
    {
      path: adminHomePath,
      titleId: 'adminHome.title',
      page: () => import('./AdminHomePage').then((module) => module.AdminHomePage),
    },
  ],
  adminHome: {
    sections: [
      { id: 'learning-content', titleId: 'adminHome.section.learningContent' },
      { id: 'classes-enrollment', titleId: 'adminHome.section.classesEnrollment' },
      { id: 'people-access', titleId: 'adminHome.section.peopleAccess' },
      { id: 'engagement', titleId: 'adminHome.section.engagement' },
      { id: 'system', titleId: 'adminHome.section.system' },
    ],
    categories: [
      {
        id: 'courses',
        section: 'learning-content',
        titleId: 'adminHome.category.courses',
        icon: BookOpen,
      },
      {
        id: 'labs',
        section: 'learning-content',
        titleId: 'adminHome.category.labs',
        icon: FlaskConical,
      },
      {
        id: 'scorm',
        section: 'learning-content',
        titleId: 'adminHome.category.scorm',
        icon: Video,
      },
      // Launch adds the LTI tool links; the category is here so it keeps the mockup's place.
      {
        id: 'lti-tools',
        section: 'learning-content',
        titleId: 'adminHome.category.lti',
        icon: Link2,
      },
      {
        id: 'materials',
        section: 'learning-content',
        titleId: 'adminHome.category.materials',
        icon: Package,
      },
      {
        id: 'material-vendors',
        section: 'learning-content',
        titleId: 'adminHome.category.materialVendors',
        icon: ShoppingCart,
      },
      {
        id: 'content-providers',
        section: 'learning-content',
        titleId: 'adminHome.category.contentProviders',
        icon: DownloadCloud,
      },
      {
        id: 'assessments',
        section: 'learning-content',
        titleId: 'adminHome.category.assessments',
        icon: CheckSquare,
      },
      {
        id: 'license-agreements',
        section: 'learning-content',
        titleId: 'adminHome.category.licenseAgreements',
        icon: Handshake,
      },
      {
        id: 'subscriptions',
        section: 'learning-content',
        titleId: 'adminHome.category.subscriptions',
        icon: RefreshCcw,
      },
      {
        id: 'classes',
        section: 'classes-enrollment',
        titleId: 'adminHome.category.classes',
        icon: MonitorPlay,
      },
      {
        id: 'class-enrollments',
        section: 'classes-enrollment',
        titleId: 'adminHome.category.classEnrollments',
        icon: PlayCircle,
      },
      {
        id: 'course-assignments',
        section: 'classes-enrollment',
        titleId: 'adminHome.category.courseAssignments',
        icon: ClipboardList,
      },
      {
        id: 'training-keys',
        section: 'classes-enrollment',
        titleId: 'adminHome.category.trainingKeys',
        icon: KeyRound,
      },
      {
        id: 'classrooms',
        section: 'classes-enrollment',
        titleId: 'adminHome.category.classrooms',
        icon: Paintbrush,
      },
      {
        id: 'class-vouchers',
        section: 'classes-enrollment',
        titleId: 'adminHome.category.classVouchers',
        icon: Ticket,
      },
      { id: 'users', section: 'people-access', titleId: 'adminHome.category.users', icon: Users },
      {
        id: 'instructors',
        section: 'people-access',
        titleId: 'adminHome.category.instructors',
        icon: GraduationCap,
      },
      {
        id: 'user-groups',
        section: 'people-access',
        titleId: 'adminHome.category.userGroups',
        icon: UsersRound,
      },
      {
        id: 'user-roles',
        section: 'people-access',
        titleId: 'adminHome.category.userRoles',
        icon: Lock,
      },
      {
        id: 'organizations',
        section: 'people-access',
        titleId: 'adminHome.category.organizations',
        icon: Network,
      },
      {
        id: 'companies',
        section: 'people-access',
        titleId: 'adminHome.category.companies',
        icon: Briefcase,
      },
      {
        id: 'surveys',
        section: 'engagement',
        titleId: 'adminHome.category.surveys',
        icon: ClipboardCheck,
      },
      {
        id: 'notifications',
        section: 'engagement',
        titleId: 'adminHome.category.notifications',
        icon: Bell,
      },
      {
        id: 'announcements',
        section: 'engagement',
        titleId: 'adminHome.category.announcements',
        icon: Megaphone,
      },
      {
        id: 'badges',
        section: 'engagement',
        titleId: 'adminHome.category.badges',
        icon: Trophy,
      },
      {
        id: 'notes',
        section: 'engagement',
        titleId: 'adminHome.category.notes',
        icon: FileText,
      },
      {
        id: 'web-application',
        section: 'system',
        titleId: 'adminHome.category.webApplication',
        icon: Settings,
      },
      {
        id: 'authentication-providers',
        section: 'system',
        titleId: 'adminHome.category.authenticationProviders',
        icon: ShieldCheck,
      },
    ],
  },
}
